import React, { useEffect } from 'react';
import MobileSheet from 'components/MobileSheet';
import { SearchFilterState } from '../searchState';
import FilterPanel from './FilterPanel';

interface MobileFilterSheetProps {
  state: SearchFilterState;
  onChange: (patch: Partial<SearchFilterState>) => void;
  onClearAll: () => void;
  onClose: () => void;
  totalCount?: number;
  /**
   * Scroll this filter group into view on open. Set by the arrival coachmark's
   * "Show me the filter", which promises to land the user on a specific group
   * rather than merely opening the sheet.
   */
  scrollToGroupId?: string;
}

/**
 * The search filter sheet. The shell (portal, scroll lock, Escape, overlay,
 * handle, footer) now lives in the shared MobileSheet; what remains here is the
 * search-specific part -- the FilterPanel body and the live result count.
 */
export const MobileFilterSheet: React.FC<MobileFilterSheetProps> = ({
  state,
  onChange,
  onClearAll,
  onClose,
  totalCount,
  scrollToGroupId,
}) => {
  /**
   * The Trade group happens to be the first section today, so at scroll 0 it is
   * already in view — but that is an accident of section order, not a promise.
   * Scrolling explicitly keeps "Show me the filter" honest if a group is ever
   * inserted above it (e.g. when "Available to send" comes back).
   *
   * `block: 'start'` inside .mobile-sheet-body, which is the scroll container.
   */
  useEffect(() => {
    if (!scrollToGroupId) return;
    const target = document.getElementById(scrollToGroupId);
    if (!target) return;
    target.scrollIntoView({ block: 'start', behavior: 'auto' });
  }, [scrollToGroupId]);

  // The count is for the filters as they stand, so the button always says what
  // dismissing the sheet will show. Undefined only before the first response.
  const applyLabel =
    totalCount === undefined
      ? 'Show results'
      : `Show ${totalCount.toLocaleString()} ${totalCount === 1 ? 'card' : 'cards'}`;

  return (
    <MobileSheet ariaLabel="Filters" onClose={onClose} applyLabel={applyLabel}>
      <FilterPanel state={state} onChange={onChange} onClearAll={onClearAll} touch />
    </MobileSheet>
  );
};

export default MobileFilterSheet;
